import { AppScreen } from '@stackflow/plugin-basic-ui'
import classNames from 'classnames'
import { ReactElement, ReactNode, useEffect } from 'react'
import PullToRefresh from 'react-simple-pull-to-refresh'
import useAppRouter, { sendPostMessageOnlyMessage } from '../../../modules/hooks/common/useAppRouter'
import { COLORS } from '../../../modules/vars'
import LoadingSpinner from '../../common/LoadingSpinner'
import LessonFloatingButton from '../../new-portal/lessons/LessonFloatingButton'
import { StackNavBarBackIcon } from '../caf/common/StackNavBar'

interface WebviewStackContainerProps {
  children: ReactNode
  title?: ReactNode
  onBack?: () => void
  appendLeft?: () => ReactNode
  appendRight?: () => ReactNode
  height?: string // 추가된 프로퍼티
  border?: boolean
  borderSize?: string
  borderColor?: string
  onRefresh?: (...any: any) => any
  isPullable?: boolean
  showBookButton?: boolean
  bookButtonBottomPixed?: string
  showChannelTalk?: boolean
  isMinHeightScreen?: boolean
  className?: string
  backgroundColor?: string
}

const DefaultBackButton = () => <div className="h-0 w-0 p-[0px]"></div>

const WebviewStackContainer: React.FC<WebviewStackContainerProps> = ({
  children,
  title = null,
  onBack,
  appendLeft = null,
  appendRight = null,
  height = '54px',
  border = true,
  borderSize = '1px',
  borderColor = COLORS.GRAY200,
  onRefresh,
  isPullable = false,
  showBookButton = false,
  bookButtonBottomPixed = '0px',
  showChannelTalk = false,
  isMinHeightScreen = true,
  className,
  backgroundColor,
}) => {
  const appBarConfig = {
    title,
    height,
    borderSize: borderSize,
    borderColor: borderColor,
    border: border,
    backButton: { render: DefaultBackButton },
    renderLeft: appendLeft ? appendLeft : onBack ? () => <StackNavBarBackIcon pop={onBack} /> : null,
    renderRight: appendRight,
  }

  // Webview ChannelTalk Hide
  useEffect(() => {
    if (!showChannelTalk && typeof window != 'undefined') {
      window?.ChannelIO?.('shutdown')
      window?.ChannelIO?.('hideChannelButton')
    }
  }, [showChannelTalk])

  const router = useAppRouter()

  useEffect(() => {
    sendPostMessageOnlyMessage(JSON.stringify({ query: router.query }))
  }, [router.query])

  return !isPullable && !onRefresh ? (
    <AppScreen appBar={appBarConfig} backgroundColor={backgroundColor}>
      <div
        style={isMinHeightScreen ? { minHeight: `calc(100vh - ${height} - env(safe-area-inset-top))` } : {}}
        className={classNames('relative select-none flex flex-col items-stretch', className)}
      >
        {children}
        {showBookButton && (
          <div
            className={`pointer-events-none sticky z-[50] flex w-full justify-end`}
            style={{ bottom: bookButtonBottomPixed }}
          >
            <div className="mr-[16px] mb-[16px] select-none">
              <LessonFloatingButton isWebviewLocation={true} />
            </div>
          </div>
        )}
      </div>
    </AppScreen>
  ) : (
    <AppScreen appBar={appBarConfig} backgroundColor={backgroundColor}>
      <PullToRefresh
        refreshingContent={
          <div className="mt-20 flex w-full items-center justify-center">
            <LoadingSpinner show={true} primary />
          </div>
        }
        isPullable={isPullable}
        pullingContent={null}
        className="h-[0px]"
        onRefresh={async () => {
          onRefresh ? onRefresh() : window?.location.reload()
        }}
      >
        <div className="relative select-none">
          {children as ReactElement<any, any>}
          {showBookButton && (
            <div
              className={`pointer-events-none sticky z-[50] flex w-full justify-end`}
              style={{ bottom: bookButtonBottomPixed }}
            >
              <div className="mr-[16px] mb-[16px] select-none">
                <LessonFloatingButton isWebviewLocation={true} />
              </div>
            </div>
          )}
        </div>
      </PullToRefresh>
    </AppScreen>
  )
}

export default WebviewStackContainer
